import { useCallback, useEffect, useRef } from 'react';
import type { MouseEvent, PointerEvent as ReactPointerEvent } from 'react';

export interface UseLongPressOptions {
  /** Hold duration in milliseconds. */
  delay?: number;
  /** Maximum pointer movement in CSS pixels before cancelling. */
  movementThreshold?: number;
  enabled?: boolean;
}

/**
 * Spread the returned handlers on the press target. Normal clicks and scrolling
 * remain available; a completed hold consumes its following pointer click.
 * For a container with separate action buttons, attach this to its preview area
 * rather than the entire container.
 */
export function useLongPress<T extends HTMLElement = HTMLElement>(
  onLongPress: (event: PointerEvent) => void,
  { delay = 500, movementThreshold = 10, enabled = true }: UseLongPressOptions = {},
) {
  const callbackRef = useRef(onLongPress);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pressRef = useRef<{ pointerId: number; x: number; y: number } | null>(null);
  const suppressClickRef = useRef(false);

  useEffect(() => { callbackRef.current = onLongPress; }, [onLongPress]);

  const cancel = useCallback(() => {
    if (timerRef.current !== null) clearTimeout(timerRef.current);
    timerRef.current = null;
    pressRef.current = null;
  }, []);

  useEffect(() => {
    if (!enabled) {
      cancel();
      suppressClickRef.current = false;
      return;
    }

    const onMove = (event: PointerEvent) => {
      const press = pressRef.current;
      if (press && event.pointerId === press.pointerId &&
        Math.hypot(event.clientX - press.x, event.clientY - press.y) > movementThreshold) {
        cancel();
      }
    };
    const onEnd = (event: PointerEvent) => {
      if (event.pointerId === pressRef.current?.pointerId) cancel();
    };
    const onAdditionalPointer = (event: PointerEvent) => {
      if (pressRef.current && event.pointerId !== pressRef.current.pointerId) cancel();
    };
    const onVisibilityChange = () => { if (document.hidden) cancel(); };

    document.addEventListener('pointerdown', onAdditionalPointer, true);
    document.addEventListener('pointermove', onMove, { passive: true });
    document.addEventListener('pointerup', onEnd);
    document.addEventListener('pointercancel', onEnd);
    document.addEventListener('scroll', cancel, { capture: true, passive: true });
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('blur', cancel);
    return () => {
      cancel();
      document.removeEventListener('pointerdown', onAdditionalPointer, true);
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onEnd);
      document.removeEventListener('pointercancel', onEnd);
      document.removeEventListener('scroll', cancel, true);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('blur', cancel);
    };
  }, [cancel, enabled, movementThreshold]);

  const onPointerDown = useCallback((event: ReactPointerEvent<T>) => {
    cancel();
    suppressClickRef.current = false;
    if (!enabled || !event.isPrimary || event.button !== 0) return;
    pressRef.current = { pointerId: event.pointerId, x: event.clientX, y: event.clientY };
    const nativeEvent = event.nativeEvent;
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      suppressClickRef.current = true;
      callbackRef.current(nativeEvent);
    }, delay);
  }, [cancel, delay, enabled]);

  const onClickCapture = useCallback((event: MouseEvent<T>) => {
    // Keyboard/assistive-technology activation must remain available.
    if (!suppressClickRef.current || event.detail === 0) return;
    suppressClickRef.current = false;
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const onContextMenu = useCallback((event: MouseEvent<T>) => {
    if (pressRef.current || suppressClickRef.current) event.preventDefault();
  }, []);

  return { onPointerDown, onPointerLeave: cancel, onClickCapture, onContextMenu };
}
